import { getAnalyticsContextDefaultProperties } from '@helios/frontend-core';
import { TaskNames } from '@helios/tasks';
import { Themes } from '@palmetto/dsm-tokens';
import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  retainSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { TimePeriod } from '../../../api/energy/types';
import featureConfig from '../../../config/featureConfig';
import { PAGE_MAX_WIDTH } from '../../../config/pageConfig';
import { TakeoverId } from '../../../hooks/useTakeover';
import { displayUnitSchema } from '../../../pages/ElectricityUsageAndSolarPage/types';
import { ROUTE } from '../../../routes';
import { TIMES_OF_DAY } from '../../../utils/time';
import { Root } from '../App/Root';
import {
  featureFlagGuard,
  redirectAuthenticated,
  redirectNonOnboardingUsers,
  redirectOnboardingUsersToWelcome,
  redirectEnrollmentWithoutPlanToChoosePlan,
  redirectProjectDetailToHomeV2,
  redirectSingleActiveProjectToDetail,
  redirectUnauthenticated,
} from './routeGuards';
import { RouterContext, RouteStaticData } from './types';

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
  validateSearch: zodValidator(
    z.object({
      article: z.string().optional(),
      intercomArticle: z.coerce.string().optional(),
      completedFeedItem: z.string().optional(),
      redirect: z.string().optional(),
      takeover: z.nativeEnum(TakeoverId).optional(),
      takeoverProps: z.record(z.string(), z.any()).optional(),
      email: z.string().optional(), // relaxed to prevent crashes
      hideNav: z.coerce.boolean().optional(),
      hideTopNav: z.coerce.boolean().optional(),
      hideAppBanner: z.coerce.boolean().optional(),
      mode: z.enum(['light', 'dark']).optional(),
      existingProjectId: z.string().optional(),
      sut: z.string().optional(),
    }),
  ),

  notFoundComponent: lazyRouteComponent(() => import('../../../pages/PageNotFound/PageNotFound')),
  errorComponent: lazyRouteComponent(() => import('../../PageError/PageErrorRoute')),

  beforeLoad: async ({ context, search }) => {
    if (!context.isAuthenticated && search.email) {
      try {
        z.string().email().parse(search.email);
        window.analytics?.identify(
          undefined,
          { email: search.email },
          {
            anonymousId: search.email,
            context: await getAnalyticsContextDefaultProperties(),
          },
        );
      } catch (err) {
        console.error('Invalid email in query params:', search.email, err);
      }
    }
  },
});

export const articleWidgetsClaimPointsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ARTICLE_WIDGETS_CLAIM_POINTS,
  validateSearch: zodValidator(
    z.object({
      widgetVariant: z.string().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/ArticleWidgets/ClaimPoints/ClaimPointsPage')),
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ACCOUNT,
  component: lazyRouteComponent(() => import('../../../pages/AccountPage/AccountPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'account.pageTitle',
    },
    backgroundColor: 'bg-canvas-default',
    bottomNav: {
      hide: true,
    },
  },
});

export const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.NOTIFICATIONS,
  component: lazyRouteComponent(() => import('../../../pages/NotificationsPage/NotificationsPage')),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
    return featureFlagGuard(featureConfig.displayNotificationCenter)(context);
  },
  staticData: {
    headerProps: {
      pageTitle: 'notifications.pageTitle',
    },
    backgroundColor: 'bg-canvas-default',
    bottomNav: {
      hide: true,
    },
  },
});

export const achievementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ACHIEVEMENTS,
  component: lazyRouteComponent(() => import('../../../pages/AchievementsPage/AchievementsPage')),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
    return featureFlagGuard(featureConfig.achievementsPage)(context);
  },
  staticData: {
    headerProps: {
      hideHeader: true,
      backgroundColor: 'bg-canvas-default',
    },
    backgroundColor: 'bg-canvas-default',
    theme: 'volt',
  },
});

export const connectedAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.CONNECTED_ACCOUNTS,
  component: lazyRouteComponent(() => import('../../../pages/ConnectedAccountsPage/ConnectedAccountsPage')),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
    return featureFlagGuard(featureConfig.connectedAccountsPage)(context);
  },
  staticData: {
    headerProps: {
      pageTitle: 'Connected Accounts',
    },
    bottomNav: {
      hide: true,
    },
  },
});

export const creditCheckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.CREDIT_CHECK,
  validateSearch: zodValidator(
    z.object({
      context: z.record(z.string(), z.any()).optional(),
      awaitingResult: z.coerce.boolean().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/CreditCheck/CreditCheck')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: { hideHeader: true },
  },
});

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.AUTH,
  validateSearch: zodValidator(
    z.object({
      flow: z.enum(['login', 'signup']).optional(),
      ex: z.enum(['EXISTING_EMAIL', 'EXISTING_SABAL_ACCOUNT']).optional(),
      phoneLast4: z.string().optional(),
      email: z.string().email().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/Authentication/AuthPage/AuthPage')),
  beforeLoad: redirectAuthenticated,
  staticData: {
    hideNavigation: true,
  },
});

export const deepLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.DEEPLINK,
  component: lazyRouteComponent(() => import('../../../pages/DeepLink/DeepLink')),
});

export const energyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ENERGY,
  validateSearch: zodValidator(
    z.object({
      forceWeeklySummaryReplay: z.boolean().optional(),
      openTask: z.enum(TaskNames).optional(),
      showTrivia: z.boolean().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/EnergyPage/EnergyPage')),
  beforeLoad: async (context) => {
    redirectUnauthenticated(context);
    return featureFlagGuard(featureConfig.energyPage)(context);
  },
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-bolder',
    safeAreaTopColor: 'bg-canvas-default',
    safeAreaTopTheme: Themes.lumen,
    theme: Themes.lumenAlt,
    navTheme: Themes.lumen,
  },
});

export const energyProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ENERGY_PROFILE,
  component: lazyRouteComponent(() => import('../../../pages/EnergyProfile/EnergyProfile')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'energyProfile.pageTitle',
    },
    bottomNav: {
      hide: true,
    },
  },
});

export const financeAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.FINANCE_ACCOUNTS,
  validateSearch: zodValidator(
    z.object({
      sabalAccountId: z.string().optional(),
      action: z.enum(['pay']).optional(),
    }),
  ),
  component: lazyRouteComponent(
    () => import('../../../pages/Billing/FinanceAccountsListPage/FinanceAccountsListPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'payments.pageTitle',
    },
  },
});

export const financeAccountDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.FINANCE_ACCOUNTS_DETAIL,
  component: lazyRouteComponent(
    () => import('../../../pages/Billing/FinanceAccountDetailPage/FinanceAccountDetailPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-bold',
  },
});

export const financeAccountUpdatePaymentMethodRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.FINANCE_ACCOUNTS_UPDATE_PAYMENT_METHOD,
  params: z.object({
    financeAccountId: z.string().nonempty(),
  }),
  validateSearch: zodValidator(
    z.object({
      planId: z.string().optional(),
      scope: z.enum(['update-payment-method']),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/UtilityRatePlan/FlowLogin/FlowLogin')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'utilityRatePlan.updatePaymentMethod.pageTitle',
    },
  },
});

export const landerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ROOT,
  component: lazyRouteComponent(() => import('../../../pages/Lander/Lander')),
  staticData: {
    hideNavigation: true,
  },
});

const homeStaticData: RouteStaticData = {
  headerProps: {
    hideHeader: true,
  },
  backgroundColor: 'bg-canvas-default',
  contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.HOME],
};

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.HOME,
  validateSearch: zodValidator(
    z.object({
      error: z.enum(['create-project-delayed']).optional(),
      timeOfDay: z.enum(TIMES_OF_DAY).optional().catch(undefined),
    }),
  ),
  search: { middlewares: [retainSearchParams(['timeOfDay'])] },
  component: lazyRouteComponent(() => import('../../../pages/HomePageVersioning/HomePageVersioning')),
  beforeLoad: (args) => {
    redirectUnauthenticated(args);
    redirectSingleActiveProjectToDetail(args);
  },
  staticData: homeStaticData,
});

export const homeIndexRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: '/',
  component: lazyRouteComponent(() => import('../../../pages/HomePageV2/HomeContent/HomeContent')),
  staticData: homeStaticData,
});

export const homeProjectDetailRoute = createRoute({
  getParentRoute: () => homeRoute,
  path: 'projects/$id',
  params: z.object({
    id: z.string().nonempty(),
  }),
  validateSearch: zodValidator(
    z.object({
      existingProject: z.coerce.boolean().optional(),
      onboardingTimeout: z.coerce.boolean().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/HomePageV2/ProjectDetailContent/ProjectDetailContent')),
  staticData: homeStaticData,
});

export const learningCenterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.LEARNING_CENTER,
  component: lazyRouteComponent(() => import('../../../pages/LearningCenterPage/LearningCenterPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-default',
  },
});

export const learningCenterCategoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.LEARNING_CENTER_CATEGORY,
  component: lazyRouteComponent(() => import('../../../pages/LearningCenterPage/LearningCenterCategoryPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-default',
  },
});

export const learningCenterArticleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.LEARNING_CENTER_ARTICLE,
  component: lazyRouteComponent(() => import('../../../pages/LearningCenterArticlePage/LearningCenterArticlePage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-default',
  },
});

export const loginSignupChoiceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.LOGIN_SIGNUP_CHOICE,
  component: lazyRouteComponent(() => import('../../../pages/Authentication/LoginSignupChoice/LoginSignupChoice')),
  beforeLoad: redirectAuthenticated,
  validateSearch: zodValidator(
    z.object({
      redirect: z.string().optional(),
    }),
  ),
});

export const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.LOGOUT,
  component: lazyRouteComponent(() => import('../../../pages/Logout/Logout')),
  beforeLoad: redirectUnauthenticated,
});

export const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ONBOARDING,
  validateSearch: zodValidator(z.object({ redirect: z.string().optional() })),
  component: lazyRouteComponent(() => import('../../../pages/Onboarding/Onboarding')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
  },
});

export const onboardingDeprecatedEnergyBillRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ONBOARDING_DEPRECATED_ENERGY_BILL,
  component: lazyRouteComponent(() => import('../../../pages/UploadUtilityBill/UploadUtilityBill')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'onboardingDeprecated.utilityBill.pageTitle',
    },
  },
});

export const titleVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.TITLE_VERIFICATION,
  component: lazyRouteComponent(() => import('../../../pages/TitleVerification/TitleVerification')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'titleVerification.pageTitle',
    },
  },
});

export const pageNotFound = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.PAGE_NOT_FOUND,
  component: lazyRouteComponent(() => import('../../../pages/PageNotFound/PageNotFound')),
  beforeLoad: redirectUnauthenticated,
});

export const paymentSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: zodValidator(
    z.object({
      context: z.record(z.string(), z.any()).optional(),
      skipInstantValidation: z.boolean().optional(),
    }),
  ),
  path: ROUTE.PAYMENT_SETUP,
  component: lazyRouteComponent(() => import('../../../pages/Billing/PaymentSetupPage/PaymentSetup')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'billing.pageTitle',
    },
  },
});

export const bankVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  validateSearch: zodValidator(
    z.object({
      context: z.record(z.string(), z.any()).optional(),
      returnUrl: z.string().optional(),
    }),
  ),
  path: ROUTE.BANK_VERIFICATION,
  component: lazyRouteComponent(() => import('../../../pages/BankVerificationPage/BankVerificationPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.PROFILE,
  component: lazyRouteComponent(() => import('../../../pages/Profile/Profile')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    backgroundColor: 'bg-canvas-default',
    headerProps: {
      pageTitle: 'profile.pageTitle',
    },
    bottomNav: {
      hide: true,
    },
  },
});

export const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.PROJECT_DETAIL,
  params: z.object({
    id: z.string().nonempty(),
  }),
  validateSearch: zodValidator(
    z.object({
      existingProject: z.coerce.boolean().optional(),
      onboardingTimeout: z.coerce.boolean().optional(),
      finalizingVerification: z.coerce.boolean().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/ProjectDetail/ProjectDetail')),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
    redirectOnboardingUsersToWelcome(context);
    redirectProjectDetailToHomeV2(context);
  },
});

export const energyPlanEducationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ENERGY_PLAN_EDUCATION,
  params: z.object({
    id: z.string().nonempty(),
  }),
  component: lazyRouteComponent(
    () => import('../../../pages/education-sequences/EnergyPlanEducation/EnergyPlanEducationPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const comfortPlanHvacEducationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.COMFORT_PLAN_HVAC_EDUCATION,
  params: z.object({
    id: z.string().nonempty(),
  }),
  component: lazyRouteComponent(
    () => import('../../../pages/education-sequences/ComfortPlanHvacEducation/ComfortPlanHvacEducationPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const billingEducationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.BILLING_EDUCATION,
  params: z.object({
    id: z.string().nonempty(),
  }),
  component: lazyRouteComponent(
    () => import('../../../pages/education-sequences/BillingEducation/BillingEducationPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const seasonalityEducationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SEASONALITY_EDUCATION,
  params: z.object({
    id: z.string().nonempty(),
  }),
  component: lazyRouteComponent(
    () => import('../../../pages/education-sequences/SeasonalityEducation/SeasonalityEducationPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const propertiesBuildingAttributesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.PROPERTIES_BUILDING_ATTRIBUTE_UPDATE,
  component: lazyRouteComponent(() => import('../../../pages/BuildingAttributeUpdate/BuildingAttributeUpdate')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'energyProfile.pageTitle',
    },
  },
});

export const questionnaireRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.QUESTIONNAIRE,
  params: z.object({
    id: z.string().nonempty(),
  }),
  component: lazyRouteComponent(() => import('../../../pages/Questionnaire/Questionnaire')),
  beforeLoad: redirectUnauthenticated,
});

export const referAFriendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.REFER,
  component: lazyRouteComponent(() => import('../../../pages/ReferAFriend/ReferAFriend')),
  beforeLoad: async (context) => {
    redirectUnauthenticated(context);
    return featureFlagGuard(featureConfig.referAFriend)(context);
  },
  staticData: {
    headerProps: {
      pageTitle: 'refer.pageTitle',
    },
  },
});

export const serviceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SERVICE,
  component: lazyRouteComponent(() => import('../../../pages/ServiceCenterPage/ServiceCenterPage')),
  beforeLoad: (args) => {
    redirectUnauthenticated(args);
  },
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-bold md:bg-canvas-default',
    bottomNav: {
      hide: true,
    },
  },
});

export const serviceHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SERVICE_HISTORY,
  component: lazyRouteComponent(() => import('../../../pages/ServiceHistoryPage/ServiceHistoryPage')),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
  },
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-default',
    hideSunnyChatOnMobile: true,
    bottomNav: {
      hide: true,
    },
  },
});

export const serviceHistoryDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SERVICE_HISTORY_DETAIL,
  component: lazyRouteComponent(
    () => import('../../../pages/ServiceHistoryPage/ServiceHistoryDetailPage/ServiceHistoryDetailPage'),
  ),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
  },
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-default',
    hideSunnyChatOnMobile: true,
    bottomNav: {
      hide: true,
    },
  },
});

export const shopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SHOP,
  component: lazyRouteComponent(() => import('../../../pages/ShopPage/ShopPage')),
  beforeLoad: () => ({
    staticDataOverrides: {
      backgroundColor: 'bg-canvas-default',
      safeAreaTopColor: 'bg-canvas-default',
      theme: Themes.palmetto,
    },
  }),
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.SHOP],
  },
});

export const shopSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SHOP_SEARCH,
  component: lazyRouteComponent(() => import('../../../pages/ShopSearchPage/ShopSearchPage')),
  validateSearch: zodValidator(
    z.object({
      msrp: z
        .union([
          z.number(),
          z.object({
            $gt: z.number().optional(),
            $gte: z.number().optional(),
            $lt: z.number().optional(),
            $lte: z.number().optional(),
            $eq: z.number().optional(),
            $ne: z.number().optional(),
          }),
        ])
        .optional(),
      manufacturers: z.array(z.string()).optional(),
    }),
  ),
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-default',
    safeAreaTopColor: 'bg-canvas-default',
    theme: Themes.palmetto,
  },
});

export const shopProductsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SHOP_PRODUCTS,
  params: z.object({
    category: z.string().nonempty(),
  }),
  component: lazyRouteComponent(() => import('../../../pages/ProductDetailPage/ProductDetailPage')),
  beforeLoad: () => ({
    staticDataOverrides: {
      backgroundColor: 'bg-canvas-default',
      safeAreaTopColor: 'bg-canvas-default',
      theme: Themes.palmetto,
      bottomNav: {
        hostTheme: 'core',
        props: {
          hostBackgroundColor: 'subtle',
        },
      },
    },
  }),
  staticData: {
    headerProps: {
      hideHeader: true,
    },
  },
});

export const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SIGNUP,
  validateSearch: zodValidator(
    z.object({
      token: z.string().optional(),
      channel: z.string().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/Authentication/InviteSignup/InviteSignup')),
  beforeLoad: redirectAuthenticated,
  staticData: {
    hideNavigation: true,
  },
});

export const solarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SOLAR,
  validateSearch: zodValidator(
    z.object({
      period: z.nativeEnum(TimePeriod).optional().catch(undefined),
      rangeEnd: z.string().date().optional().catch(undefined),
      displayUnit: displayUnitSchema.optional().catch(undefined),
      forceSolarSavings: z.boolean().optional().catch(undefined),
    }),
  ),
  component: lazyRouteComponent(
    () => import('../../../pages/ElectricityUsageAndSolarPage/ElectricityUsageOrSolarPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-bold',
    contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.SOLAR],
  },
});

export const electricityUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.ELECTRICITY_USAGE,
  validateSearch: zodValidator(
    z.object({
      period: z.nativeEnum(TimePeriod).optional().catch(undefined),
      rangeEnd: z.string().date().optional().catch(undefined),
      displayUnit: displayUnitSchema.optional().catch(undefined),
      forceSolarSavings: z.boolean().optional().catch(undefined),
    }),
  ),
  component: lazyRouteComponent(
    () => import('../../../pages/ElectricityUsageAndSolarPage/ElectricityUsageOrSolarPage'),
  ),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    backgroundColor: 'bg-canvas-bold',
    contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.ELECTRICITY_USAGE],
  },
});

export const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.SUPPORT,
  beforeLoad: redirectUnauthenticated,
  component: lazyRouteComponent(() => import('../../../pages/SupportPage/SupportPage')),
  staticData: {
    headerProps: { hideHeader: true },
    backgroundColor: 'bg-canvas-bold',
    bottomNav: { hide: true },
  },
});

export const plaidDemoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.PLAID_DEMO,
  component: lazyRouteComponent(() => import('../../../pages/PlaidDemo/PlaidDemo')),
  beforeLoad: (context) => featureFlagGuard(featureConfig.plaidDemo)(context),
  staticData: {
    headerProps: { hideHeader: true },
    backgroundColor: 'bg-canvas-default',
  },
});

export const testAchievementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/test-achievements',
  component: lazyRouteComponent(() => import('../../../pages/AchievementsPage/AchievementsPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      backgroundColor: 'bg-canvas-default',
    },
    theme: 'volt',
  },
});

export const utilityBillUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.UTILITY_BILL_UPLOAD,
  component: lazyRouteComponent(() => import('../../../pages/UploadUtilityBill/UploadUtilityBill')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'utilityBill.pageTitle',
    },
  },
});

export const utilityRatePlanChoosePlanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.UTILITY_RATE_PLAN_CHOOSE_PLAN,
  validateSearch: zodValidator(
    z.object({
      context: z.record(z.string(), z.any()).optional(),
      returnUrl: z.string().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/UtilityRatePlan/ChooseRatePlan/ChooseRatePlan')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.UTILITY_RATE_PLAN_CHOOSE_PLAN],
  },
});

export const utilityRatePlanEnrollmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.UTILITY_RATE_PLAN_ENROLLMENT,
  validateSearch: zodValidator(
    z.object({
      planId: z.string().optional(),
      scope: z.enum(['enrollment', 'update-payment-method', 'documents', 'billing']).optional(),
      returnUrl: z.string().optional(),
      address_1: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postal_code: z.string().optional(),
      context: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/UtilityRatePlan/FlowLogin/FlowLogin')),
  beforeLoad: (args) => {
    redirectUnauthenticated(args);
    redirectEnrollmentWithoutPlanToChoosePlan(args);
  },
  staticData: {
    headerProps: {
      hideHeader: true,
    },
  },
});

export const utilityRatePlanEnrollmentLandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.UTILITY_RATE_PLAN_ENROLLMENT_LANDING,
  component: lazyRouteComponent(() => import('../../../pages/UtilityRatePlan/UtilityRatePlanLanding')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      pageTitle: 'utilityRatePlan.enrollment.pageTitle',
    },
    contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.UTILITY_RATE_PLAN_ENROLLMENT_LANDING],
  },
});

export const utilityRatePlanOptOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.UTILITY_RATE_PLAN_OPT_OUT,
  validateSearch: zodValidator(
    z.object({
      returnUrl: z.string().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/UtilityRatePlan/OptOut/OptOutPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    headerProps: {
      hideHeader: true,
    },
    contentMaxWidth: PAGE_MAX_WIDTH[ROUTE.UTILITY_RATE_PLAN_OPT_OUT],
  },
});

export const oauthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.OAUTH_CALLBACK,
  component: lazyRouteComponent(() => import('../../../pages/OAuthCallbackPage/OAuthCallbackPage')),
  staticData: {
    hideNavigation: true,
  },
});

export const projectWelcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.WELCOME,
  component: lazyRouteComponent(() => import('../../../pages/ProjectWelcome/ProjectWelcome')),
  validateSearch: zodValidator(
    z.object({
      welcomeProject: z.string().optional(),
    }),
  ),
  beforeLoad: (context) => {
    redirectUnauthenticated(context);
    redirectNonOnboardingUsers(context);
  },
  staticData: {
    hideNavigation: true,
    theme: Themes.core,
  },
});

export const projectLoadingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ROUTE.PROJECT_LOADING,
  validateSearch: zodValidator(
    z.object({
      acquisitionChannel: z.string().optional(),
    }),
  ),
  component: lazyRouteComponent(() => import('../../../pages/ProjectLoadingPage/ProjectLoadingPage')),
  beforeLoad: redirectUnauthenticated,
  staticData: {
    hideNavigation: true,
    headerProps: { hideHeader: true },
  },
});

export const routes = [
  accountRoute,
  achievementsRoute,
  articleWidgetsClaimPointsRoute,
  connectedAccountsRoute,
  creditCheckRoute,
  authRoute,
  deepLinkRoute,
  electricityUsageRoute,
  energyRoute,
  energyProfileRoute,
  financeAccountRoute,
  financeAccountDetailRoute,
  financeAccountUpdatePaymentMethodRoute,
  homeRoute.addChildren([homeIndexRoute, homeProjectDetailRoute]),
  landerRoute,
  learningCenterRoute,
  learningCenterCategoryRoute,
  learningCenterArticleRoute,
  loginSignupChoiceRoute,
  logoutRoute,
  notificationsRoute,
  onboardingRoute,
  onboardingDeprecatedEnergyBillRoute,
  titleVerificationRoute,
  paymentSetupRoute,
  bankVerificationRoute,
  pageNotFound,
  profileRoute,
  projectWelcomeRoute,
  projectLoadingRoute,
  propertiesBuildingAttributesRoute,
  projectDetailRoute,
  energyPlanEducationRoute,
  comfortPlanHvacEducationRoute,
  billingEducationRoute,
  seasonalityEducationRoute,
  questionnaireRoute,
  referAFriendRoute,
  serviceRoute,
  serviceHistoryRoute,
  serviceHistoryDetailRoute,
  shopRoute,
  shopProductsRoute,
  shopSearchRoute,
  signupRoute,
  solarRoute,
  supportRoute,
  testAchievementsRoute,
  utilityBillUploadRoute,
  utilityRatePlanChoosePlanRoute,
  utilityRatePlanEnrollmentRoute,
  utilityRatePlanEnrollmentLandingRoute,
  utilityRatePlanOptOutRoute,
  oauthCallbackRoute,
  plaidDemoRoute,
];
